import React, { useContext } from 'react'
import { Hide, Box, Divider, Container } from 'pcln-design-system'
import { AppDownload } from '@pcln/vip'
import { useContentful } from '@pcln/contentful-components'
import SmsOptIn from '@/components/SmsOptIn'
import renderMerchModules from '@/components/MerchModuleWrapper/renderUtils'
import AdBanner from '@/components/AdBanner'
import WhyPricelineBanner from '@/components/WhyPricelineBanner'
import PopularDestinations from '@/components/PopularDestinations'
import CreditCard from '@/components/CreditCard/CreditCard'
import { ANALYTICS_CATEGORY_HOME } from '@/constants/analytics'
import DISNEY_CONTENT from '@/constants/promotions/disney'
import BRANDED_MARKETING_CONTENT from '@/constants/promotions/branded-marketing'
import {
  EmailSignUpToast,
  EmailSignUpComponent
} from '@/components/EmailSignUp'
import PromoFeatureBox from '@/components/PromoFeatureBox'
import PackagesCarousel from '@/components/PackagesCarousel'
import RecentSearchCards from '@/components/RecentSearchCards'
import CrossSell from '@/components/CrossSell'
import useMostRecentFutureBooking from '@/hooks/useMostRecentFutureBooking'
import useExpressDealModal from '@/hooks/useExpressDealModal'
import ExpressDealModal from '@/components/Hotels/ExpressDeal/ExpressDealModal'
import MarketingTout from '@/components/Promos/MarketingTout/MarketingTout'
import BootstrapDataContext from '@/context/BootstrapDataContext'
import PhoneEntry from '@/components/PhoneFormEntry/PhoneEntry'
import useExtendStayBooking from '@/hooks/useExtendStayBooking'
import { REASONS_HOME } from '@/constants/reasons'
import ConditionalRender, { Experiment } from '@/components/ConditionalRender'
import ExtendStay from '@/components/ExtendStay'
import useSeti from '@/hooks/useSeti'
import { CustomMerchModule, CustomToutType } from '@/types'
import { SilhouetteMarketingModule } from '@pcln/marketing'

const expressDealQSKey = 'expressdeal'
const expressDealQSValue = 'y'

export default function HomeContent() {
  const mostRecentFutureBooking = useMostRecentFutureBooking()
  const extendData = useExtendStayBooking()
  const { isOpen } = useExpressDealModal(expressDealQSKey, expressDealQSValue)
  const {
    webstats: { clientGUID },
    locale: { ipAddr },
    referral,
    customer
  } = useContext(BootstrapDataContext)
  const isWhyPclnLower = useSeti('HP_BOTTOM_WHY_PRICELINE') === 'VARIANT'

  const AdBannerContent = () => {
    return (
      <>
        <AdBanner>
          <div id="div-gpt-ad-1614716188275-0" />
        </AdBanner>
        <Divider borderColor="border.base" my={0} />
      </>
    )
  }

  const heroSpacing = {
    mt: [0, null, null, -4, 0],
    mb: 3,
    style: { position: 'relative' }
  }

  const CrossSellContent = () => {
    return (
      <>
        {mostRecentFutureBooking ? (
          <Box mx="auto" p={3} maxWidth="1280px" {...heroSpacing}>
            <CrossSell booking={mostRecentFutureBooking} />
          </Box>
        ) : (
          <PromoFeatureBox />
        )}
      </>
    )
  }

  const promoCollection: any = useContentful('promoCollection')
  const promoItems: CustomToutType[] | CustomMerchModule[] =
    promoCollection?.items || []

  return (
    <>
      <ExpressDealModal
        isModalOpen={isOpen}
        gaCategory={ANALYTICS_CATEGORY_HOME}
        qsKey={expressDealQSKey}
        qsValue={expressDealQSValue}
      />
      {!extendData && (
        <Box>
          <RecentSearchCards />
        </Box>
      )}
      <Hide md>
        {extendData ? (
          <Box mx="auto" p={3} maxWidth="1280px" {...heroSpacing}>
            <ExtendStay {...extendData} />
          </Box>
        ) : (
          <CrossSellContent />
        )}
      </Hide>
      <ConditionalRender experimentCode="HP_SMS_COMPONENT">
        <Experiment>
          {mostRecentFutureBooking ? (
            <Box mb={[5, 4, 0, 4]} />
          ) : (
            <Box mb={[5, 5, 0, 5]} />
          )}
          <SmsOptIn />
        </Experiment>
      </ConditionalRender>
      {!isWhyPclnLower && <WhyPricelineBanner content={REASONS_HOME} />}
      <MarketingTout />
      <ConditionalRender experimentCode="HP_BRANDED_MKTG_COMPONENT">
        <Experiment>
          <Box maxWidth="1280px" margin="auto" px={4}>
            <SilhouetteMarketingModule {...BRANDED_MARKETING_CONTENT} />
          </Box>
        </Experiment>
      </ConditionalRender>
      <PackagesCarousel
        heading="Don't miss these hotel + flight deals"
        rudderstack={{
          rudderEvent: true,
          type: 'explicit',
          subType: 'cross_sell',
          category: 'impression',
          parentProduct: 'FLY:STAY',
          cguid: clientGUID,
          emailAddress: customer.emailAddress,
          source: 'homepage',
          label: 'Flight + Hotel Deal'
        }}
      />
      <Container>
        <Box
          color="background.light"
          mx={4}
          boxShadowSize="sm"
          my={2}
          borderRadius="xl"
          py={4}
        >
          <Box mx={4}>
            <AppDownload
              imageMinHeight="383px"
              ctaArea={<PhoneEntry {...{ clientGUID, ipAddr, referral }} />}
            />
          </Box>
        </Box>
      </Container>
      <EmailSignUpComponent
        sourceId="LANDING_HOME"
        category={ANALYTICS_CATEGORY_HOME}
      />
      <EmailSignUpToast sourceId="LANDING_HOME_TOAST_DESKTOP" />
      <PopularDestinations />
      {renderMerchModules(promoItems, [DISNEY_CONTENT])}
      <CreditCard />
      {isWhyPclnLower && (
        <Box>
          <WhyPricelineBanner content={REASONS_HOME} />
          <Divider borderColor="border.base" my={0} />
        </Box>
      )}
      <AdBannerContent />
    </>
  )
}
